import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IonContent, IonModal } from "@ionic/react";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import DisplayContent from "../../../molecules/DisplayContent";
import { getStudyPublicNotice } from "../../../../services/study.service";
import { PublicNoticeType } from "../../../../types/welcome";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  studyId: number;
}

/**
 * « Note d'information » ouverte depuis la ligne de la page d'accueil.
 *
 * C'est un DOCUMENT DE RÉFÉRENCE, à lire — pas un contrat que le visiteur accepterait
 * en s'inscrivant : le document à accepter (le consentement) est présenté à la fin de
 * l'inscription, avec ses cases à cocher. La ligne meta le dit explicitement pour que
 * la lecture ici ne puisse jamais passer pour un consentement.
 *
 * Volet (sheet) et non modale centrée : le contenu est long et le geste de fermeture
 * d'Ionic est celui d'une feuille. D'où la structure imposée — IonContent en enfant
 * DIRECT de l'IonModal (Ionic fait un querySelector('ion-content') pour poser le
 * geste), et surtout pas de ModalCard, qui est réservé aux modales centrées
 * (voir atoms/ModalKit.tsx).
 */
const StudyNoticeSheet: FC<Props> = ({ isOpen, onDismiss, studyId }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [contentData, setContentData] = useState<PublicNoticeType | null>(null);

  // Chargé à l'ouverture seulement, et re-chargé quand (studyId, language) change :
  // la page d'accueil a un sélecteur de langue, et la note doit suivre — même
  // discipline que DisplayConsent, dont la clé de cache porte aussi la langue.
  useEffect(() => {
    if (!isOpen || !studyId) return undefined;

    let isCancelled = false;
    setIsLoading(true);
    setIsError(false);

    getStudyPublicNotice({ studyId, lang: language })
      .then(({ data }) => {
        if (isCancelled) return;
        setContentData(data);
        // Un 200 sans corps ne doit pas donner une feuille vide : c'est un échec
        // comme un autre du point de vue du lecteur. On teste les BLOCS et non
        // l'enveloppe : côté API, mappingContentBlock renvoie toujours un objet
        // ({blocks: []} pour un contenu sans bloc, {blocks: ''} quand l'association
        // est absente), donc `content` est vrai dans toutes les réponses réelles.
        // Une note enregistrée avec un éditeur vide donnerait sinon un volet avec
        // un titre, la ligne meta et rien d'autre.
        const blocks = data?.content?.blocks;
        setIsError(!data?.content || !Array.isArray(blocks) || blocks.length === 0);
      })
      .catch(() => {
        if (isCancelled) return;
        // 404 = l'étude n'a rien publié. On ne devrait pas arriver ici (la ligne
        // n'est affichée que si le welcome porte un id), mais si la configuration a
        // changé depuis la mise en cache du welcome, on montre un message, pas un vide.
        setContentData(null);
        setIsError(true);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen, studyId, language]);

  return (
    <IonModal
      // --scroll: une note d'information n'a pas de longueur bornée, la feuille doit
      // donc faire défiler son corps au lieu de se dimensionner dessus (theme/modal.scss).
      className="resonance-sheetmodal resonance-sheetmodal--scroll"
      isOpen={isOpen}
      backdropDismiss
      onWillDismiss={onDismiss}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      canDismiss
    >
      {/* Header + ion-content en enfants DIRECTS de la modale : c'est la structure
          qu'Ionic met en page (et dans laquelle il va chercher l'ion-content) — un
          wrapper intermédiaire rend le corps non défilable. */}
      <Header>
        <HeaderTitle>{t("home_study_notice_title")}</HeaderTitle>
        <CloseButton onClick={onDismiss} aria-label={t("common_close")}>
          ✕
        </CloseButton>
      </Header>

      <Body>
        <Meta>{t("home_study_notice_reference")}</Meta>

        {isLoading && <ResonanceLoader />}
        {!isLoading && isError && <ErrorState>{t("home_study_notice_error")}</ErrorState>}
        {!isLoading && !isError && contentData?.content && (
          <DisplayContent editorJsData={contentData.content} />
        )}
      </Body>
    </IonModal>
  );
};

export default StudyNoticeSheet;

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px 12px;
  flex: 0 0 auto;
  background: var(--app-color-background, #ffffff);
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--app-color-text, #041e32);
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  font-size: 18px;
  color: var(--app-color-text, #041e32);
  cursor: pointer;
  padding: 4px;
`;

/* Le corps défilant. Les marges passent par les variables --padding-* d'Ionic et non
   par un padding CSS : ion-content peint et fait défiler un shadow root interne, un
   padding posé de l'extérieur rognerait la zone de défilement au lieu de l'inséter. */
const Body = styled(IonContent)`
  flex: 1;
  min-height: 0;
  --background: var(--app-color-background, #ffffff);
  --padding-top: 4px;
  --padding-start: 20px;
  --padding-end: 20px;
  --padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
`;

/* Cadre de lecture : dit d'emblée que ce document ne s'accepte pas ici. */
const Meta = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
`;

const ErrorState = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.45);
`;
